import type { Ari } from '@atlassian/ari';

import {
	type AudioFormat,
	type AudioRequest,
	type CustomLength,
	type CustomTone,
	getAudio,
} from '../../api/cc-smarts/getAudio';
import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import { AudioStreamingState } from '../state/AudioPlayerStreamingStore';

import {
	appendBufferAsync,
	getBufferAheadSeconds,
	waitForBufferToDrain,
} from './AudioBufferEffects';
import { getConfluenceContentAri } from './getConfluenceContentAri';

export type AudioPlayerStreamingAction =
	| { type: AudioStreamingState.UNINITIALIZED }
	| { type: AudioStreamingState.LOADING }
	| { type: AudioStreamingState.ERROR; error: Error }
	| { type: AudioStreamingState.STREAM_STARTED; response: ReadableStream<Uint8Array> }
	| { type: AudioStreamingState.STREAM_DISPOSED };

export type AudioPlayerRequestParams = {
	locale: string;
	contentId: string;
	contentType: string;
	type: AudioFeatureType;
	instructions: {
		tone: CustomTone;
		length: CustomLength;
	};
	format: AudioFormat;
	activationId: string;
	product: string;
	cloudId: string;
	supportingResourceARIs?: string[];
};

type AudioRequestWithSignal = AudioRequest & { signal?: AbortSignal };
type AudioFetchQuery = (
	requestParams: AudioRequestWithSignal,
) => Promise<ReadableStream<Uint8Array>>;

export class AudioPlayerStreamingManager {
	private response: ReadableStream<Uint8Array> | null = null;
	private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
	private abortController: AbortController | null = null;
	private validatedRequestParams: AudioRequest | null = null;
	private readonly unvalidatedRequestParams: AudioPlayerRequestParams;
	private audioElement: HTMLAudioElement | null = null;
	private mediaSource: MediaSource | null = null;
	private isReadyToConsumeStream: boolean = false;

	private readonly fetchAudioQuery: AudioFetchQuery;
	private readonly onAudioStreamingChange: (action: AudioPlayerStreamingAction) => void;
	private readonly getAri: (
		siteId: string,
		contentId: string,
		contentType: string,
	) => Ari | undefined;
	private readonly audioMimeType: string;
	private readonly maxBufferAheadSeconds: number;
	private readonly resumeBufferAheadSeconds: number;

	private isAbortError = (error: unknown): boolean =>
		(error instanceof DOMException && error.name === 'AbortError') ||
		(error instanceof Error && error.message === 'BodyStreamBuffer was aborted');

	constructor(config: {
		requestParams: AudioPlayerRequestParams;
		onAudioStreamingChange: (action: AudioPlayerStreamingAction) => void;
		fetchAudioQuery?: AudioFetchQuery;
		getAri?: (siteId: string, contentId: string, contentType: string) => Ari | undefined;
		audioMimeType?: string;
		maxBufferAheadSeconds?: number;
		resumeBufferAheadSeconds?: number;
	}) {
		this.onAudioStreamingChange = config.onAudioStreamingChange;
		this.fetchAudioQuery = config.fetchAudioQuery ?? ((params) => getAudio(params));
		this.getAri = config.getAri ?? getConfluenceContentAri;
		this.audioMimeType = config.audioMimeType ?? 'audio/mpeg';
		this.maxBufferAheadSeconds = config.maxBufferAheadSeconds ?? 360;
		this.resumeBufferAheadSeconds = config.resumeBufferAheadSeconds ?? 180;
		this.unvalidatedRequestParams = config.requestParams;
	}

	public validateRequestParams(): void {
		const {
			locale,
			contentId,
			contentType,
			type,
			format,
			activationId,
			cloudId,
			instructions,
			supportingResourceARIs = [],
		} = this.unvalidatedRequestParams;

		const mainResourceARI = this.getAri(cloudId, contentId, contentType) as unknown as string;
		if (!mainResourceARI) {
			this.onAudioStreamingChange({
				type: AudioStreamingState.ERROR,
				error: new Error('Invalid content ARI'),
			});
			return;
		}

		this.validatedRequestParams = {
			mainResourceARI,
			supportingResourceARIs,
			locale,
			type,
			format,
			activationId,
			instructions,
		};
	}

	public async fetchAudio(): Promise<void> {
		if (!this.validatedRequestParams) {
			return;
		}

		// Abort any previous in-flight request
		this.abortController?.abort();

		// Create a new abort controller for this fetch request
		this.abortController = new AbortController();
		// Store reference to signal so we can check it even if controller is nulled during dispose
		const signal = this.abortController.signal;

		this.onAudioStreamingChange({ type: AudioStreamingState.LOADING });
		try {
			this.response = await this.fetchAudioQuery({
				...this.validatedRequestParams,
				signal,
			});

			// Check if we were aborted before updating state
			// (disposeStream may have been called while fetch was in progress)
			if (signal.aborted) {
				return;
			}

			this.onAudioStreamingChange({
				type: AudioStreamingState.STREAM_STARTED,
				response: this.response,
			});
		} catch (error) {
			// Don't report abort as an error - it's an intentional cancellation
			if (this.isAbortError(error)) {
				return;
			}
			this.onAudioStreamingChange({
				type: AudioStreamingState.ERROR,
				error: error as Error,
			});
		}
	}

	public streamAudioToMediaSource(audioElement: HTMLAudioElement | null): void {
		if (!audioElement || !this.response) {
			return;
		}

		this.mediaSource = new MediaSource();
		this.audioElement = audioElement;
		this.isReadyToConsumeStream = true;

		const url = URL.createObjectURL(this.mediaSource);
		this.audioElement.src = url;
		this.audioElement.load();

		this.mediaSource.addEventListener('sourceopen', () => this.onMediaSourceOpen(url), {
			once: true,
		});
	}

	private onMediaSourceOpen = async (url: string) => {
		if (!this.mediaSource || !this.response || !this.audioElement) {
			return;
		}

		try {
			URL.revokeObjectURL(url);
			const sourceBuffer = this.mediaSource.addSourceBuffer(this.audioMimeType);
			sourceBuffer.mode = 'sequence';
			this.reader = this.response.getReader();

			while (this.isReadyToConsumeStream) {
				// Backpressure: if bufferAhead is at or above the cap, wait for it to drain
				const bufferAheadSeconds = getBufferAheadSeconds(this.audioElement);
				if (bufferAheadSeconds >= this.maxBufferAheadSeconds) {
					await waitForBufferToDrain(
						this.audioElement,
						this.isReadyToConsumeStream,
						this.resumeBufferAheadSeconds,
					);
					if (!this.isReadyToConsumeStream) {
						break;
					}
				}
				const { done, value } = await this.reader.read();
				if (done) {
					break;
				}

				if (!this.isReadyToConsumeStream) {
					break;
				}
				// Ensure we have a proper BufferSource by copying to a new ArrayBuffer
				const buffer = new Uint8Array(value);
				await appendBufferAsync(sourceBuffer, buffer);
			}

			if (this.isReadyToConsumeStream) {
				// done
				this.disposeStream();
			}
		} catch (e) {
			if (this.isAbortError(e)) {
				return;
			}

			this.onAudioStreamingChange({ type: AudioStreamingState.ERROR, error: e as Error });
		}
	};

	public resetState(): void {
		this.onAudioStreamingChange({ type: AudioStreamingState.UNINITIALIZED });
	}

	public pauseAndResetPlayer(): void {
		if (this.audioElement) {
			this.audioElement.pause();
			this.audioElement.currentTime = 0;
			this.audioElement.src = '';
		}
	}

	public disposeStream(): void {
		this.isReadyToConsumeStream = false;

		// Abort any in-flight fetch request
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}

		// Cancel the reader to properly close the underlying fetch stream
		if (this.reader) {
			this.reader.cancel().catch(() => {
				// Errors during cleanup are ignored
			});
			this.reader = null;
		}

		if (this.mediaSource?.readyState === 'open') {
			try {
				this.mediaSource.endOfStream();
			} catch {
				// Errors during cleanup can be safely ignored.
			}
		}
		this.response = null;
		this.onAudioStreamingChange({ type: AudioStreamingState.STREAM_DISPOSED });
	}
}
